import React, { Component } from 'react';
import { Container, Popover, Button, TextField, Box, Typography } from '@material-ui/core'

/**
 *  The NotifyPatronPopover presents a modal dialog for entering a message to patron when a slot is modified/deleted. The caller must pass in props:
 *  
 *  a boolean called "isOpen" which determines whether the dialog is open or not
 *  slot (Slot): The slot being modified or deleted, null for Resources/Services
 *  resource (Resource/Service): The resource being deleted ONLY, null for Slots/Bookings
 *  a boolean called "isForApproval" which is true if the notification is for an approval/unapproval action, false if for modification/deletion
 *  a boolean called "isDeletingSlot" which is true if the slot is being deleted, false if it is being modified (ignored for isForApproval=true)
 *  a boolean called "willRegenerateRecurrence" which is true if the slot modification will cause future slots to be cancelled then regenerated
 *  a boolean called "willJustModifyThisOne" which is true if only modifying this one slot, false if modifying this and future slots
 *  a function called "sendEmailCallback" which takes a string of user-entered text, called when the Send Email button is pressed with the text as argument
 *  a function called "suppressEmailCallback" which is called when the Supress button is pressed
 *  a function called "cancelCallback" which is called when the Cancel button is pressed
 *  
 *  After one of the callback buttons is pressed, the entered text is cleared to allow reuse of the component
 * 
 */


export class NotifyPatronPopover extends Component {

    styles = {
        button: {
            marginTop: 10,
            marginLeft: 10,
            marginRight: 10
        },
        container: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }
    };


    state = {
        textEntry: ""
    }
   
    _textFieldChanged = (event) => {
        if (this.state.textEntry.length >= 1024)
            return;
        
        this.setState({textEntry: event.target.value});
    }
    
    
    _sendEmailButtonPressed = () => {
        this.props.sendEmailCallback(this.state.textEntry);
        this.setState({textEntry: ""});  //clear text for next time
    }
    
    _suppressEmailButton = () => {
        this.props.suppressEmailCallback();
        this.setState({textEntry: ""});  //clear text for next time
    }
    
    _cancelButtonPressed = () => {
        this.props.cancelCallback();
        this.setState({textEntry: ""});  //clear text for next time
    }
    
    render() {
        
        let maxWidth = window.innerWidth * 0.7;
        if (maxWidth > 800)
            maxWidth = 800;
        
        let open = false;
        if (this.props.isOpen === true)
            open = true;
    
        let msg = null;
        if (this.props.slot) {

            if (this.props.isForApproval) {     // only bookings are approved/unapproved
                msg = "The system can automatically notify the patron of the approval change by email. You can also add a custom note to the email.";
            }
            else if (!this.props.slot.recurrenceId || this.props.willJustModifyThisOne) {    // no recurrence, so we must have patrons on this slot
                if (this.props.isDeletingSlot)
                    msg = "This " + this.props.slot.type + " has patrons. The system can automatically notify patrons of the cancellation by email. You can also add a custom note to the email.";
                else
                    msg = "This " + this.props.slot.type + " has patrons. If you changed the name, times" + (this.props.slot.type === "Booking" ? ", Service," : "") + " or Resources the system can automatically notify patrons of the change by email. You can also add a custom note to the email.";
            }
            else {
                if (this.props.isDeletingSlot)
                    msg = "This and future " + this.props.slot.type + "s are active with patrons. The system can automatically notify patrons who have signed up of the cancellation by email. You can also add a custom note to the email.";
                else if (this.props.willRegenerateRecurrence)
                    msg = <span>{"This and future " + this.props.slot.type + "s are active with patrons. You changed the recurrence pattern which "}<span style={{color: 'red'}}>{"cancels then regenerates the Event series and attendees are not carried over. "}</span>The system can automatically notify patrons of the <span style={{color: 'red'}}>cancellation</span>. You can also add a custom note to the email.</span>;
                else
                    msg = "This and future " + this.props.slot.type + "s are active with patrons. If you changed the name, times, or Resources the system can automatically notify patrons of the change by email. You can also add a custom note to the email.";  
            }
        }
        else if (this.props.resource) {
             msg = "This " + this.props.resource.type + " is scheduled for use in the future. The system can automatically notify affected patrons of by email. You can also add a custom note to the email.";
        }
        
        let notifyText = "Notify Change by Email";
        if (this.props.isDeletingSlot || (this.props.willRegenerateRecurrence && !this.props.willJustModifyThisOne))
            notifyText = "Notify Cancellation by Email";
        if (this.props.resource)
            notifyText = "Notify Future Cancellations by Email";

        return (
            <Popover open={open} 
                anchorReference="anchorPosition"
                anchorPosition={{left: window.innerWidth/2, top: window.innerHeight/2}}
                transformOrigin={{vertical: 'center', horizontal: 'center'}} >
                <Box style={{margin: 20, width: maxWidth}}>
                    <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>Notify Patrons</Typography>   
                    <Typography variant='body2' style={{marginTop: 10, marginBottom: 20, color: 'gray', fontStyle: 'italic'}}>{msg}</Typography>
                    <TextField autoFocus label='Custom Email Message' multiline='true' rows={10} value={this.state.textEntry} onChange={this._textFieldChanged} variant="outlined" fullWidth={true} inputProps={{maxLength: 1024}} InputLabelProps={{ shrink: true}} />
                    <Container style={this.styles.container}>    
                        <Button onClick={this._sendEmailButtonPressed} variant="outlined" style={{...this.styles.button, color: 'green'}}>{notifyText}</Button>
                        <Button onClick={this._suppressEmailButton} variant="outlined" style={{...this.styles.button, color: 'red'}}>Suppress Email Notification</Button>
                        <Button onClick={this._cancelButtonPressed} variant="outlined" style={this.styles.button}>Cancel</Button>
                    </Container>
                </Box>
            </Popover>
        ); 
    }
}